// NodeCover.jsx

import React, { useEffect, useLayoutEffect, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { PhotoShape } from 'utilities/Enums';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// CSS
import './NodeCover.css';
import 'react-loading-skeleton/dist/skeleton.css';

// Components
import Skeleton from 'react-loading-skeleton';

/**
 * NodeCover
 * 
 * Displays a node's cover image.
 * 
 * @param {string} src - The source URL of the cover image.
 * @param {PhotoShape} shape - The shape of the cover image: "CIRCLE" or "SQUARE".
 * @param {string|number} width - The width of the cover image (e.g., "100%", "200px").
 * @param {string|number} height - The height of the cover image (e.g., "100%", "200px").
 * @param {boolean} clickable - Whether the cover image is clickable.
 * @param {function} onClick - The function to call when the cover image is clicked.
 * 
 * @returns {ReactNode} NodeCover.
 */
const NodeCover = React.memo(({ src, shape = PhotoShape.SQUARE, width = '460px', height = '460px', clickable = false, onClick }) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- DEVICE DETECTION ----- //

  // Device Detection

  const isMobile = useIsMobile();

  // ----- STATE ----- //

  const [resolvedSrc, setResolvedSrc] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // ----- EFFECTS ----- //

  /**
   * Reset when src changes.
   */

  useLayoutEffect(() => {
    setIsLoaded(false);
    setResolvedSrc(null);
  }, [src]);

  /**
   * Resolve Image URL.
   */
  useEffect(() => {

    // Cancellation flag
    let cancelled = false;

    // Validate src
    const raw = typeof src === 'string' ? src : '';
    if (!raw) {
      setResolvedSrc(null);
      return undefined;
    }

    // Direct URLs
    if (raw.startsWith('blob:') || raw.startsWith('data:')) {
      setResolvedSrc(raw);
      return undefined;
    }

    // Resolve URL
    (async () => {
      try {
        const url = await resolveImageUrl({ nodeKey: null, url: raw });
        if (!cancelled) setResolvedSrc(url || raw);
      } catch (e) {
        if (!cancelled) setResolvedSrc(raw);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [src]);

  if (!src) return null;

  const raw = typeof src === 'string' ? src : '';
  const isDirectUrl = raw.startsWith('blob:') || raw.startsWith('data:') || raw.includes('://');
  const displaySrc = resolvedSrc || (isDirectUrl ? raw : null);

  // ----- RENDER ----- //

  return (

    <div
      className={`node-cover-container ${shape.toLowerCase()}`}>

      {/* PREVIEW IMAGE */}
      <div
        className="node-cover-image"
        style={{
          height: height,
          width: isMobile ? "100%" : width,
        }}
      >

        {!isLoaded && (
          <Skeleton
            width={isMobile ? "100%" : width}
            height={height}
            baseColor={theme?.background || undefined}
            highlightColor={theme?.backgroundMuted || undefined}
          />
        )}

        {displaySrc && (
          <img
            className={clickable ? 'node-cover clickable' : 'node-cover'}
            style={{
              cursor: clickable ? 'pointer' : 'default',
              height: height,
              opacity: isLoaded ? 1 : 0,
              width: isMobile ? "100%" : width,
            }}
            src={displaySrc}
            alt="Cover"
            role={clickable ? 'button' : undefined}
            title={clickable ? "Click here to edit..." : "Cover Photo"}
            onClick={clickable ? onClick : undefined}
            onError={() => setIsLoaded(true)}
            onLoad={() => setIsLoaded(true)}
          />
        )}

      </div>

    </div>

  );
});

// Dev Tools
NodeCover.displayName = 'NodeCover';

export default NodeCover;
